import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiRequest } from '@/services/api';

// API Response interfaces based on documentation
export interface PageRoutePermission {
  id: string;
  createdAt: string;
  name: string;
  description: string;
  label: string;
  pageRouteId: string;
  isActive: boolean;
}

export interface PageRouteFavourite {
  createdAt: string;
  id: string;
  list_order: number;
  pageRoute: string;
  pageRouteID: string;
  userID: string;
}

// Page Route interface based on API documentation
export interface PageRoute {
  id: string;
  name: string;
  description: string;
  component: string;
  icon: string;
  path: string;
  is_active: boolean;
  is_visible: boolean;
  IsUnderConstruction: boolean;
  createdAt: string;
  updatedAt?: string;
  favourite?: PageRouteFavourite;
  page_route_permissions?: PageRoutePermission[];
  permissionCount?: number;
}

// API Response wrapper
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
  totalPages?: number;
  currentPage?: number;
}

// Page state interface
interface PageState {
  pageRoutes: PageRoute[];
  currentPageRoute: PageRoute | null;
  privatePageRoutes: PageRoute[];
  loading: boolean;
  error: any;
  totalPages: number;
  currentPageNumber: number;
  pageSize: number;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  statusChangeLoading: boolean;
  restoreLoading: boolean;
}

// Initial state
const initialState: PageState = {
  pageRoutes: [],
  currentPageRoute: null,
  privatePageRoutes: [],
  loading: false,
  error: null,
  totalPages: 0,
  currentPageNumber: 1,
  pageSize: 10,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  statusChangeLoading: false,
  restoreLoading: false,
};

export const fetchPageRoutes = createAsyncThunk(
  'page/fetchPageRoutes',
  async (params: { page?: number; limit?: number; name?: string; path?: string; component?: string; isActive?: boolean } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.name) queryParams.append('name', params.name);
      if (params.path) queryParams.append('path', params.path);
      if (params.component) queryParams.append('component', params.component);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

      const response = await apiRequest(`/page-routes?${queryParams.toString()}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const fetchPrivatePageRoutes = createAsyncThunk(
  'page/fetchPrivatePageRoutes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/page-routes/private-routes');
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const fetchPageRouteById = createAsyncThunk(
  'page/fetchPageRouteById',
  async (pageRouteId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/page-routes/${pageRouteId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const createPageRoute = createAsyncThunk(
  'page/createPageRoute',
  async (pageRouteData: {
    name: string;
    description: string;
    component: string;
    icon: string;
    path: string;
    isActive: boolean;
    isVisible: boolean;
    isUnderConstruction: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/page-routes', {
        method: 'POST',
        body: JSON.stringify(pageRouteData),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const updatePageRoute = createAsyncThunk(
  'page/updatePageRoute',
  async ({ pageRouteId, pageRouteData }: { 
    pageRouteId: string; 
    pageRouteData: Partial<{
      name: string;
      description: string;
      component: string;
      icon: string;
      path: string;
      isActive: boolean;
      isVisible: boolean;
      isUnderConstruction: boolean;
    }> 
  }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/page-routes/${pageRouteId}`, {
        method: 'PUT',
        body: JSON.stringify(pageRouteData),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const deletePageRoute = createAsyncThunk(
  'page/deletePageRoute',
  async (pageRouteId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/page-routes/${pageRouteId}`, {
        method: 'DELETE',
      });
      return pageRouteId;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const changePageRouteStatus = createAsyncThunk(
  'page/changePageRouteStatus',
  async ({ pageRouteId, status }: { pageRouteId: string; status: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/page-routes/${pageRouteId}/change-status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      return { pageRouteId, response };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const restorePageRoute = createAsyncThunk(
  'page/restorePageRoute',
  async (pageRouteId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/page-routes/${pageRouteId}/restore`, {
        method: 'PATCH',
      });
      return { pageRouteId, response };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Page slice
const pageSlice = createSlice({
  name: 'page',
  initialState,
  reducers: {
    clearCurrentPageRoute: (state) => {
      state.currentPageRoute = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPageNumber: (state, action: PayloadAction<number>) => {
      state.currentPageNumber = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fetchPageRoutes
    builder
      .addCase(fetchPageRoutes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageRoutes.fulfilled, (state, action) => {
        state.loading = false;
        // API'den gelen camelCase field'ları snake_case'e transform et
        const items = action.payload.data?.items || [];
        state.pageRoutes = items.map((item: any) => ({
          ...item,
          is_active: item.isActive !== undefined ? item.isActive : item.is_active,
          is_visible: item.isVisible !== undefined ? item.isVisible : item.is_visible,
        }));
        state.totalPages = action.payload.data?.pageCount || 0;
        state.currentPageNumber = action.payload.data?.page || 1;
      })
      .addCase(fetchPageRoutes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchPrivatePageRoutes
    builder
      .addCase(fetchPrivatePageRoutes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrivatePageRoutes.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as ApiResponse<PageRoute[]>;
        const items: any[] = response.data || response;
        // Transform camelCase to snake_case
        state.privatePageRoutes = items.map((item: any) => ({
          ...item,
          is_active: item.isActive !== undefined ? item.isActive : item.is_active,
          is_visible: item.isVisible !== undefined ? item.isVisible : item.is_visible,
        }));
      })
      .addCase(fetchPrivatePageRoutes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchPageRouteById
    builder
      .addCase(fetchPageRouteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageRouteById.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as ApiResponse<PageRoute>;
        const data: any = response.data || response;
        // Transform camelCase to snake_case
        state.currentPageRoute = {
          ...data,
          is_active: data.isActive !== undefined ? data.isActive : data.is_active,
          is_visible: data.isVisible !== undefined ? data.isVisible : data.is_visible,
        };
      })
      .addCase(fetchPageRouteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // createPageRoute
    builder
      .addCase(createPageRoute.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createPageRoute.fulfilled, (state, action) => {
        state.createLoading = false;
        const response = action.payload as ApiResponse<PageRoute>;
        const data: any = response.data || response;
        // Transform camelCase to snake_case
        const newPageRoute = {
          ...data,
          is_active: data.isActive !== undefined ? data.isActive : data.is_active,
          is_visible: data.isVisible !== undefined ? data.isVisible : data.is_visible,
        };
        state.pageRoutes.unshift(newPageRoute);
      })
      .addCase(createPageRoute.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      });

    // updatePageRoute
    builder
      .addCase(updatePageRoute.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updatePageRoute.fulfilled, (state, action) => {
        state.updateLoading = false;
        const response = action.payload as ApiResponse<PageRoute>;
        const data: any = response.data || response;
        // Transform camelCase to snake_case
        const updatedPageRoute = {
          ...data,
          is_active: data.isActive !== undefined ? data.isActive : data.is_active,
          is_visible: data.isVisible !== undefined ? data.isVisible : data.is_visible,
        };
        
        const index = state.pageRoutes.findIndex(page => page.id === updatedPageRoute.id);
        if (index !== -1) {
          state.pageRoutes[index] = updatedPageRoute;
        }
        
        if (state.currentPageRoute?.id === updatedPageRoute.id) {
          state.currentPageRoute = updatedPageRoute;
        }
      })
      .addCase(updatePageRoute.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      });

    // deletePageRoute
    builder
      .addCase(deletePageRoute.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deletePageRoute.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const deletedId = action.payload as string;
        state.pageRoutes = state.pageRoutes.filter(page => page.id !== deletedId);
        if (state.currentPageRoute?.id === deletedId) {
          state.currentPageRoute = null;
        }
      })
      .addCase(deletePageRoute.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });

    // changePageRouteStatus
    builder
      .addCase(changePageRouteStatus.pending, (state) => {
        state.statusChangeLoading = true;
        state.error = null;
      })
      .addCase(changePageRouteStatus.fulfilled, (state, action) => {
        state.statusChangeLoading = false;
        const { pageRouteId, response } = action.payload as { pageRouteId: string; response: any };
        const updatedData: any = response.data || response;
        
        // Transform camelCase to snake_case
        const updatedPageRoute = {
          ...updatedData,
          is_active: updatedData.isActive !== undefined ? updatedData.isActive : updatedData.is_active,
          is_visible: updatedData.isVisible !== undefined ? updatedData.isVisible : updatedData.is_visible,
        };
        
        const index = state.pageRoutes.findIndex(page => page.id === pageRouteId);
        if (index !== -1) {
          state.pageRoutes[index] = { ...state.pageRoutes[index], ...updatedPageRoute };
        }
        
        if (state.currentPageRoute?.id === pageRouteId) {
          state.currentPageRoute = { ...state.currentPageRoute, ...updatedPageRoute };
        }
      })
      .addCase(changePageRouteStatus.rejected, (state, action) => {
        state.statusChangeLoading = false;
        state.error = action.payload;
      });

    // restorePageRoute
    builder
      .addCase(restorePageRoute.pending, (state) => {
        state.restoreLoading = true;
        state.error = null;
      })
      .addCase(restorePageRoute.fulfilled, (state, action) => {
        state.restoreLoading = false;
        const { pageRouteId, response } = action.payload as { pageRouteId: string; response: any };
        const data: any = response.data || response;
        // Transform camelCase to snake_case
        const restoredPageRoute = {
          ...data,
          is_active: data.isActive !== undefined ? data.isActive : data.is_active,
          is_visible: data.isVisible !== undefined ? data.isVisible : data.is_visible,
        };
        
        const index = state.pageRoutes.findIndex(page => page.id === pageRouteId);
        if (index !== -1) {
          state.pageRoutes[index] = { ...state.pageRoutes[index], ...restoredPageRoute };
        }
        
        if (state.currentPageRoute?.id === pageRouteId) {
          state.currentPageRoute = { ...state.currentPageRoute, ...restoredPageRoute };
        }
      })
      .addCase(restorePageRoute.rejected, (state, action) => {
        state.restoreLoading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearCurrentPageRoute, 
  clearError, 
  setCurrentPageNumber, 
  setPageSize 
} = pageSlice.actions;

export default pageSlice.reducer; 